import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { Box, Column, Divider, GridItem } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useLocaleKeys } from '~/i18n';
import { ClientAdditionalRecipients } from './ClientAdditionalRecipients/ClientAdditionalRecipients';
import { ClientContactPicker } from './ClientContactPicker/ClientContactPicker';
import { ClientPicker } from './ClientPicker/ClientPicker';
import styles from './ClientRecipients.module.scss';
import { SAMPLE_CLIENT_ID } from './ClientRecipients.utils';
export const GetClientRecipientsTID = (parentTestId)=>getTestIds(parentTestId);
export const ClientRecipients = (param)=>{
    let { selectedClientId, onSelectedClientIdChange, primaryContactId, onPrimaryContactIdChange, reviewers, onReviewersChange, isSample, onSampleChange, onCreateClient, onEditClient, onCreateContact, showReviewerErrors, showSignatoryError = false, amendmentInfoCallout, disabled = false, testId } = param;
    const { t: clientPickerTexts } = useLocaleKeys('components.clientPicker');
    const { t: contactPickerTexts } = useLocaleKeys('agreementStudio.proposalMode.contactPicker');
    const { ClientRecipientsTestIds } = useMemo(()=>GetClientRecipientsTID(testId), [
        testId
    ]);
    const { clients, isLoading: clientsLoading, error: clientsError, refresh: refreshClients } = useClients({
        refreshOnHookMount: true
    });
    const { sampleContact } = useContactsMap();
    const sampleMode = isSample || selectedClientId === SAMPLE_CLIENT_ID;
    const { contacts: clientContacts, isLoading: clientContactsLoading } = useClientContacts(selectedClientId, {
        enabled: !sampleMode,
        refreshOnHookMount: true
    });
    const contactsLoading = !sampleMode && selectedClientId != null && clientContactsLoading;
    const scopedContacts = useMemo(()=>{
        if (sampleMode) return [
            sampleContact
        ];
        return selectedClientId != null ? clientContacts : [];
    }, [
        sampleMode,
        sampleContact,
        selectedClientId,
        clientContacts
    ]);
    const primaryContact = useMemo(()=>{
        var _scopedContacts_find;
        return sampleMode ? sampleContact : (_scopedContacts_find = scopedContacts.find((contact)=>contact.id() === primaryContactId)) !== null && _scopedContacts_find !== void 0 ? _scopedContacts_find : null;
    }, [
        sampleMode,
        sampleContact,
        scopedContacts,
        primaryContactId
    ]);
    const handleSelectClient = useCallback((clientId)=>{
        if (clientId === selectedClientId) {
            return;
        }
        onSelectedClientIdChange(clientId);
        onSampleChange(false);
        onPrimaryContactIdChange(null);
        onReviewersChange([]);
    }, [
        selectedClientId,
        onSelectedClientIdChange,
        onSampleChange,
        onPrimaryContactIdChange,
        onReviewersChange
    ]);
    const isRealClient = selectedClientId != null && !sampleMode;
    useEffect(()=>{
        if (!isRealClient || contactsLoading || primaryContactId != null || scopedContacts.length !== 1) {
            return;
        }
        onPrimaryContactIdChange(scopedContacts[0].id());
    }, [
        isRealClient,
        contactsLoading,
        primaryContactId,
        scopedContacts,
        onPrimaryContactIdChange
    ]);
    const handleSelectSample = useCallback(()=>{
        onSampleChange(true);
        onSelectedClientIdChange(SAMPLE_CLIENT_ID);
        onPrimaryContactIdChange(null);
        onReviewersChange([]);
    }, [
        onSampleChange,
        onSelectedClientIdChange,
        onPrimaryContactIdChange,
        onReviewersChange
    ]);
    const handleSignatoryChange = useCallback((contact)=>{
        var _ref;
        const nextPrimaryId = (_ref = contact === null || contact === void 0 ? void 0 : contact.id()) !== null && _ref !== void 0 ? _ref : null;
        onPrimaryContactIdChange(nextPrimaryId);
        if (nextPrimaryId != null && reviewers.some((reviewer)=>reviewer.clientContactId === nextPrimaryId)) {
            onReviewersChange(reviewers.filter((reviewer)=>reviewer.clientContactId !== nextPrimaryId));
        }
    }, [
        onPrimaryContactIdChange,
        reviewers,
        onReviewersChange
    ]);
    const showRecipientFields = selectedClientId != null;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 12,
        children: /*#__PURE__*/ _jsxs(Column, {
            testId: ClientRecipientsTestIds.Root(),
            gap: 16,
            className: styles.root,
            children: [
                /*#__PURE__*/ _jsx(ClientPicker, {
                    testId: ClientRecipientsTestIds.ClientPicker(),
                    clients: clients,
                    isLoading: clientsLoading,
                    error: clientsError,
                    onRetry: refreshClients,
                    selectedClientId: selectedClientId,
                    onSelectClient: handleSelectClient,
                    onSelectSample: handleSelectSample,
                    onCreateClient: onCreateClient,
                    onEditClient: onEditClient,
                    disabled: disabled
                }),
                showRecipientFields && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(ClientContactPicker, {
                            testId: ClientRecipientsTestIds.Signatory(),
                            contacts: scopedContacts,
                            value: primaryContact,
                            onChange: handleSignatoryChange,
                            onCreateContact: onCreateContact,
                            disabled: disabled || sampleMode || contactsLoading,
                            isLoading: contactsLoading,
                            label: contactPickerTexts.mainSignatoryLabel(),
                            info: contactPickerTexts.mainSignatoryMustSign(),
                            placeholder: contactPickerTexts.signatoryPlaceholder(),
                            error: showSignatoryError && primaryContact == null ? clientPickerTexts.signatoryRequired() : undefined
                        }, selectedClientId),
                        /*#__PURE__*/ _jsx(ClientAdditionalRecipients, {
                            testId: ClientRecipientsTestIds.AdditionalRecipients(),
                            clientContacts: scopedContacts,
                            primaryContact: primaryContact,
                            reviewers: reviewers,
                            onChange: onReviewersChange,
                            onCreateContact: onCreateContact,
                            showReviewerErrors: showReviewerErrors,
                            disabled: disabled || sampleMode
                        }),
                        amendmentInfoCallout && /*#__PURE__*/ _jsx(Box, {
                            testId: ClientRecipientsTestIds.AmendmentCallout(),
                            children: amendmentInfoCallout
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Divider, {
                    margin: 0
                })
            ]
        })
    });
};
